// ============================================
// MATCH SCORE BADGE COMPONENT
// ============================================

import { cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

interface MatchScoreBadgeProps {
  score: number;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function MatchScoreBadge({ 
  score, 
  showLabel = true, 
  size = 'md',
  className 
}: MatchScoreBadgeProps) {
  const { t } = useTranslation('panel');

  // Puana göre renk belirle
  const getColorClasses = () => {
    if (score >= 80) {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    if (score >= 60) {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    }
    if (score >= 40) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'text-xs px-1.5 py-0.5';
      case 'lg':
        return 'text-base px-3 py-1.5';
      default:
        return 'text-sm px-2 py-1';
    }
  };

  const getProgressColor = () => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-blue-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-gray-500';
  };

  return (
    <div className={cn('inline-flex items-center gap-2', className)}>
      <span
        className={cn(
          'inline-flex items-center font-semibold rounded-full border',
          getColorClasses(),
          getSizeClasses()
        )}
      >
        %{Math.round(score)}
        {showLabel && <span className="ml-1 font-normal">{t('candidates.matchLabel')}</span>}
      </span>
      {size === 'lg' && (
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden min-w-[100px]">
          <div
            className={cn('h-full rounded-full transition-all', getProgressColor())}
            style={{ width: `${Math.min(score, 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}
